import { MutableRefObject, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  listPointState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import useSWR from 'swr'
import { Point } from 'types/building'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import { rotatedMarkerIcon, rotatedMarkerIconDense } from '../CustomMarker'

type Props = {
  leafletRef: MutableRefObject<any>
  setRadarReady?: (ready: boolean) => void
}

export default function useRadar({ leafletRef, setRadarReady }: Props) {
  const { id: buildingId, floorId } = useParams()
  const { navigate } = useDirectory()

  const buildingMode = useRecoilValue(buildingModeState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [, setPoints] = useRecoilState(listPointState)

  const { trigger: handlePoint, isMutating } = useMutation('v1/points')
  const { mutate: refetchPointDetail } = useSWR<Point>(
    currentSelectedPoint
      ? {
          url: `v1/points/${currentSelectedPoint}`,
          params: {
            building_id: buildingId,
            floor_id: floorId
          }
        }
      : null,
    {
      revalidateOnMount: false
    }
  )

  const radarEventHandler = (mode: string) => {
    const isEditable = pointDetail && mode === 'point'

    return {
      click() {
        if (isEditable) {
          const handleClick = () => {
            if (!leafletRef.current.radarDragFlag.isRadarMode) {
              leafletRef.current.radarDragFlag.isRadarMode = true
              leafletRef.current.mapContainerRef.dragging.disable()
              leafletRef.current.povMarkerRef.setIcon(
                rotatedMarkerIconDense(leafletRef.current.radarDragFlag.rotate)
              )
              return
            }
            leafletRef.current.radarDragFlag.isRadarMode = false
            leafletRef.current.mapContainerRef.dragging.enable()
            leafletRef.current.povMarkerRef.setIcon(
              rotatedMarkerIcon(leafletRef.current.radarDragFlag.rotate)
            )
            // clear timeout to stop mousedown event
            leafletRef.current.radarDragFlag.mouseHoldTime &&
              clearTimeout(leafletRef.current.radarDragFlag.mouseHoldTime)
            // update new h gap between image360 and radar

            const { v, z, h } = pointDetail.initView

            !isMutating &&
              handlePoint(
                {
                  method: 'patch',
                  query: [currentSelectedPoint],
                  data: {
                    floor_id: +(floorId || 0),
                    building_id: +(buildingId || 0),
                    init_view: {
                      h,
                      v,
                      z,
                      radar_h_gap: Krpano.NormalizeHGap(
                        leafletRef.current.radarDragFlag.hgap
                      )
                    }
                  }
                },
                {
                  onSuccess: async () => {
                    await refetchPointDetail().then((currentPoint) => {
                      if (!currentPoint) return

                      const updatedPoint: Point = {
                        ...currentPoint,
                        floorId: Number(floorId)
                      }
                      setPointDetail(updatedPoint)
                      setPoints((prev) =>
                        Utils.formatFloorPoints(
                          prev.map((p) =>
                            p.id === currentSelectedPoint
                              ? { ...p, ...updatedPoint }
                              : p
                          ),
                          floorId
                        )
                      )

                      const activeImageId =
                        selectedSubImage || updatedPoint?.image360?.id
                      const delta = Krpano.GetSubImageDelta(
                        updatedPoint,
                        activeImageId
                      )
                      Krpano.RevalidateDirection(
                        updatedPoint?.directions || [],
                        delta
                      )
                      Krpano.RevalidateStairway(
                        updatedPoint?.stairways || [],
                        delta
                      )
                    })
                  },
                  onError: (error) => {
                    Utils.handleErrorNavigate(error, navigate)
                  }
                }
              )
          }
          Krpano.LoadChecking(handleClick)
        }
      },
      mousedown() {
        if (leafletRef.current.radarDragFlag.isRadarMode) {
          leafletRef.current.radarDragFlag.mouseHoldTime = setTimeout(() => {
            leafletRef.current.radarDragFlag.isDragging = true
          }, 200)
        }
      },
      mouseup() {
        if (
          buildingMode.selectedMode === 'link' &&
          leafletRef.current.createPolylineRef
        ) {
          leafletRef.current.mapContainerRef?.removeLayer(
            leafletRef.current.createPolylineRef
          )
          leafletRef.current.createPolylineRef = null
          leafletRef.current.mapContainerRef?.dragging.enable()
        }
      }
    }
  }

  const sync360ViewToArc = (icon: any) => {
    ;(window as any).sync_360viewToArc = (hlookat: any) => {
      setRadarReady?.(true)
      const h = Krpano.NormalizeHGap(hlookat)
      if (
        !leafletRef.current.radarDragFlag.isRadarMode &&
        leafletRef.current.radarDragFlag.hgap !== null
      ) {
        leafletRef.current.radarDragFlag.rotate = Krpano.NormalizeHGap(
          h + leafletRef.current.radarDragFlag.hgap
        )
        leafletRef.current.povMarkerRef?.setIcon(
          icon(leafletRef.current.radarDragFlag.rotate)
        )
      }
      if (leafletRef.current.radarDragFlag.isRadarMode) {
        leafletRef.current.radarDragFlag.hgap = Krpano.NormalizeHGap(
          leafletRef.current.radarDragFlag.rotate - h
        )
      }
    }
  }

  useEffect(() => {
    //  find position & add pov marker into map
    const handleDisplayPov = () => {
      if (!currentSelectedPoint || currentSelectedPoint !== pointDetail?.id) {
        leafletRef.current.radarDragFlag = {
          isRadarMode: false,
          isDragging: false,
          rotate: null,
          hgap: null,
          mouseHoldTime: null
        }
        leafletRef.current.mapContainerRef?.dragging.enable()
        return
      }
      if (pointDetail) {
        const hlookat = new KrpanoEngine('preview').instance?.get(
          'view.hlookat'
        )
        const rotate =
          hlookat !== pointDetail.initView.h
            ? Krpano.NormalizeHGap(hlookat)
            : pointDetail.initView.h

        const frontMain = Krpano.GetFrontYaw(
          pointDetail,
          pointDetail.image360?.id
        )
        const activeImageId = selectedSubImage || pointDetail.image360?.id
        const frontActive = Krpano.GetFrontYaw(pointDetail, activeImageId)
        const delta = frontActive - frontMain
        const effectiveHGap = Krpano.NormalizeHGap(
          (pointDetail.initView.radarHGap || 0) - delta
        )

        const currentRotate = leafletRef.current.radarDragFlag.rotate
        leafletRef.current.radarDragFlag = {
          isRadarMode: false,
          isDragging: false,
          rotate: Krpano.NormalizeHGap(
            typeof currentRotate === 'number'
              ? currentRotate
              : rotate + effectiveHGap
          ),
          hgap: effectiveHGap,
          mouseHoldTime: null
        }
        if (leafletRef.current.povMarkerRef) {
          leafletRef.current.povMarkerRef.setIcon(
            rotatedMarkerIcon(
              Krpano.NormalizeHGap(leafletRef.current.radarDragFlag.rotate)
            )
          )
        }
      }
    }
    handleDisplayPov()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedPoint, pointDetail, selectedSubImage])

  useEffect(() => {
    if (
      pointDetail &&
      leafletRef.current.radarDragFlag.isRadarMode &&
      !buildingMode.selectedMode
    ) {
      const hlookat = Krpano.NormalizeHGap(
        new KrpanoEngine('preview').instance?.get('view.hlookat')
      )
      const frontMain = Krpano.GetFrontYaw(
        pointDetail,
        pointDetail.image360?.id
      )
      const activeImageId = selectedSubImage || pointDetail.image360?.id
      const frontActive = Krpano.GetFrontYaw(pointDetail, activeImageId)
      const delta = frontActive - frontMain
      const effectiveHGap = Krpano.NormalizeHGap(
        (pointDetail.initView.radarHGap || 0) - delta
      )

      leafletRef.current.radarDragFlag.isRadarMode = false
      leafletRef.current.radarDragFlag.hgap = effectiveHGap
      leafletRef.current.radarDragFlag.rotate = Krpano.NormalizeHGap(
        hlookat + effectiveHGap
      )
      leafletRef.current.mapContainerRef?.dragging.enable()
      if (leafletRef.current.povMarkerRef) {
        leafletRef.current.povMarkerRef.setIcon(
          rotatedMarkerIcon(leafletRef.current.radarDragFlag.rotate)
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingMode.selectedMode, pointDetail, selectedSubImage])

  return {
    sync360ViewToArc,
    radarEventHandler
  }
}
